import { useMemo, useState } from 'react';
import type { GuaArticle } from '@/mocks/gua88-news';
import { feedArticles } from '@/mocks/gua88-news';
import { SectionHead, TagBadge, TrustBadge } from './articleUi';

export default function Feed({
  query,
  onRead,
}: {
  query: string;
  onRead: (a: GuaArticle) => void;
}) {
  const allTags = useMemo(() => {
    const set = new Set<string>();
    feedArticles.forEach((a) => set.add(a.tag));
    return Array.from(set);
  }, []);

  const [tag, setTag] = useState('全部');
  const options = ['全部', ...allTags];

  const count = (t: string) =>
    t === '全部' ? feedArticles.length : feedArticles.filter((a) => a.tag === t).length;

  const list = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return feedArticles.filter((a) => {
      if (tag !== '全部' && a.tag !== tag) return false;
      if (!kw) return true;
      const hay = (a.title + a.excerpt + a.body.join('')).toLowerCase();
      return hay.includes(kw);
    });
  }, [tag, query]);

  return (
    <section id="feed" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <SectionHead
          kicker="Collection · 分类浏览"
          title="续集新料汇总"
          desc="按栏目筛选你关心的方向，每条都标注可信度与发布时间，理性吃瓜、按需阅读。"
        />

        {/* filter chips */}
        <div className="mb-8 flex flex-wrap items-center gap-2">
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => setTag(o)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                tag === o
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'bg-background-50 text-foreground-700 hover:bg-secondary-100'
              }`}
            >
              {o}
              <span
                className={`ml-1.5 text-xs ${
                  tag === o ? 'text-background-50/70' : 'text-foreground-400'
                }`}
              >
                {count(o)}
              </span>
            </button>
          ))}
          {query.trim() && (
            <span className="ml-auto hidden items-center gap-1 text-sm text-foreground-600 sm:inline-flex">
              正在搜索「<strong className="font-medium text-foreground-950">{query}</strong>」，
              找到 {list.length} 条
            </span>
          )}
        </div>

        {/* list */}
        {list.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-900">
              <i className="ri-ghost-line"></i>
            </span>
            <p className="mt-4 font-medium text-foreground-950">这个分类暂时没有新料</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词或栏目试试吧，后续会持续补充。</p>
            <button
              type="button"
              onClick={() => {
                setTag('全部');
              }}
              className="mt-5 whitespace-nowrap rounded-full border border-background-300 px-5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看全部
            </button>
          </div>
        ) : (
          <div className="flex flex-col">
            {list.map((a, i) => (
              <article
                key={a.id}
                className="group grid grid-cols-1 gap-0 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200 sm:grid-cols-[260px_1fr]"
              >
                <button
                  type="button"
                  onClick={() => onRead(a)}
                  className="relative block overflow-hidden sm:h-full"
                  aria-label={a.title}
                >
                  <div className="aspect-[16/10] h-full sm:aspect-auto">
                    <img
                      src={a.image}
                      alt={a.title}
                      title={a.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="absolute inset-x-0 bottom-0 hidden h-16 bg-gradient-to-t from-black/35 to-transparent sm:block"></span>
                </button>
                <div className="flex flex-col p-4 md:p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <TagBadge tag={a.tag} />
                    <TrustBadge trust={a.trust} />
                    <span className="ml-auto text-xs text-foreground-400">#{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRead(a)}
                    className="mt-3 text-left"
                  >
                    <h4 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                      {a.title}
                    </h4>
                  </button>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                    {a.excerpt}
                  </p>
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-time-line"></i>
                      {a.time}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRead(a)}
                      className="inline-flex items-center whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                    >
                      阅读全文
                      <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}